type UnitCategory = 'temperature' | 'distance' | 'speed'

interface UnitDefinition {
  category: UnitCategory
  // Convert a value in this unit to the category's base unit
  toBase: (value: number) => number
  // Convert a value in the category's base unit to this unit
  fromBase: (value: number) => number
}

// The unit that raw values arrive in for each category, when no
// explicit fromUnit is given to convertValuesToTruweatherUnits.
const BASE_UNIT: Record<UnitCategory, string> = {
  temperature: 'k',
  distance: 'm',
  speed: 'm/s',
}

const MI_TO_M = 1609.34
const NM_TO_M = 1852
const FT_TO_M = 1 / 3.28084
const KTS_TO_MPS = 0.514444
const MPH_TO_MPS = 1 / 2.2369362920544
const KMH_TO_MPS = 1 / 3.6

const UNIT_DEFINITIONS: Record<string, UnitDefinition> = {
  // Temperature (base: Kelvin)
  k: { category: 'temperature', toBase: (v) => v, fromBase: (v) => v },
  c: { category: 'temperature', toBase: (v) => v + 273.15, fromBase: (v) => v - 273.15 },
  f: {
    category: 'temperature',
    toBase: (v) => ((v - 32) * 5) / 9 + 273.15,
    fromBase: (v) => ((v - 273.15) * 9) / 5 + 32,
  },

  // Visibility / Ceiling (base: meters)
  m: { category: 'distance', toBase: (v) => v, fromBase: (v) => v },
  km: { category: 'distance', toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
  mi: { category: 'distance', toBase: (v) => v * MI_TO_M, fromBase: (v) => v / MI_TO_M },
  sm: { category: 'distance', toBase: (v) => v * MI_TO_M, fromBase: (v) => v / MI_TO_M },
  miles: { category: 'distance', toBase: (v) => v * MI_TO_M, fromBase: (v) => v / MI_TO_M },
  nm: { category: 'distance', toBase: (v) => v * NM_TO_M, fromBase: (v) => v / NM_TO_M },
  ft: { category: 'distance', toBase: (v) => v * FT_TO_M, fromBase: (v) => v / FT_TO_M },

  // Wind speed (base: m/s)
  'm/s': { category: 'speed', toBase: (v) => v, fromBase: (v) => v },
  mps: { category: 'speed', toBase: (v) => v, fromBase: (v) => v },
  kts: { category: 'speed', toBase: (v) => v * KTS_TO_MPS, fromBase: (v) => v / KTS_TO_MPS },
  mph: { category: 'speed', toBase: (v) => v * MPH_TO_MPS, fromBase: (v) => v / MPH_TO_MPS },
  'km/h': { category: 'speed', toBase: (v) => v * KMH_TO_MPS, fromBase: (v) => v / KMH_TO_MPS },
  kph: { category: 'speed', toBase: (v) => v * KMH_TO_MPS, fromBase: (v) => v / KMH_TO_MPS },
}

const normalizeUnit = (unit: string): string => (unit ?? '').trim().toLowerCase()

// Converts `value` from `fromUnit` into `toUnit`. If `fromUnit` is omitted,
// `value` is assumed to already be in the category's base unit (Kelvin for
// temperature, meters for visibility/ceiling, m/s for wind speed) - that's
// what raw values queried off the map are in.
// If fromUnit and toUnit resolve to the same unit, the value is returned
// unchanged rather than being run back through a conversion factor.
const convertValuesToTruweatherUnits = function (
  value: number,
  toUnit: string,
  fromUnit?: string
): number {
  const to = normalizeUnit(toUnit)
  const toDef = UNIT_DEFINITIONS[to]

  if (!toDef) {
    return value
  }

  const from = fromUnit ? normalizeUnit(fromUnit) : BASE_UNIT[toDef.category]

  if (from === to) {
    return value
  }

  const fromDef = UNIT_DEFINITIONS[from]

  if (!fromDef || fromDef.category !== toDef.category) {
    return value
  }

  return toDef.fromBase(fromDef.toBase(value))
}

const formatValue = (value: number, layerName: string) => {
  switch (layerName) {
    case 'temperature':
      value = value.toFixed(1)
      break
    case 'wind_uv':
    case 'wind_speed_gust':
    case 'cloud_ceiling':
    case 'windUV':
      value = Math.round(value)
      break
    case 'visibility':
      value = value.toFixed(2)
      break
    case 'radar':
      value = value.toFixed(1)
      break
    default:
      value = value
      break
  }

  return value
}

enum LayerToUnitsMapping {
  'temperature' = 'F',
  'wind_uv' = 'kts',
  'visibility' = 'sm',
  'cloud_ceiling' = 'ft',
  'myradar' = 'dBz',
  'radar' = 'dBz',
  'wind_speed_gust' = 'kts',
  'windUV' = 'kts',
}

export { convertValuesToTruweatherUnits, LayerToUnitsMapping, formatValue }
